import React, { createContext, useContext, useState, useEffect } from 'react';
import { accountsApi, userApi } from '../api';

const AccountContext = createContext();

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
};

export const AccountProvider = ({ children }) => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [viewMode, setViewMode] = useState('account'); // 'account' or 'org'
  const [loading, setLoading] = useState(true);

  const dispatchUserUpdate = (userData) => {
    if (!userData) return;
    try {
      window.dispatchEvent(new CustomEvent('tp_user_updated', {
        detail: { userData }
      }));
    } catch (error) {
      console.error('Failed to dispatch user update event:', error);
    }
  };

  const applyLocalAccountSelection = (accountId, { refreshPermissions = false } = {}) => {
    setSelectedAccountId(accountId);
    if (refreshPermissions) {
      setTimeout(async () => {
        try {
          const me = await userApi.me();
          dispatchUserUpdate(me);
        } catch (error) {
          console.error('Error refreshing permissions after account change:', error);
        }
      }, 100);
    }
  };

  const updateSelectedAccount = async (accountId) => {
    try {
      if (!accountId) return;
      if (accountId === selectedAccountId) return;

      applyLocalAccountSelection(accountId);

      await userApi.updateSelectedAccount(accountId);

      // Refresh permissions for the newly selected account
      setTimeout(async () => {
        try {
          const me = await userApi.me();
          dispatchUserUpdate(me);
        } catch (error) {
          console.error('Error refreshing permissions after account change:', error);
        }
      }, 100);
    } catch (error) {
      // Still update locally even if server update fails (likely offline)
      console.error('Failed to update selected account on server:', error);
      applyLocalAccountSelection(accountId);
    }
  };

  // Load accounts on mount
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        setLoading(true);
        const accountsData = await accountsApi.listOrgAccounts();
        setAccounts(accountsData || []);

        let initialAccountId = '';
        try {
          const me = await userApi.me();
          initialAccountId = me?.accountId || '';
          if (initialAccountId) {
            applyLocalAccountSelection(initialAccountId);
            dispatchUserUpdate(me);
            return;
          }
        } catch (error) {
          console.error('Failed to load user data for account selection:', error);
        }

        if (!initialAccountId && accountsData && accountsData.length > 0) {
          const fallbackAccountId = accountsData[0].accountId;
          applyLocalAccountSelection(fallbackAccountId);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    loadAccounts();
  }, []);

  const selectedAccount = accounts.find(acc => acc.accountId === selectedAccountId);

  const value = {
    accounts,
    selectedAccountId,
    selectedAccount,
    viewMode,
    setSelectedAccountId: updateSelectedAccount,
    setViewMode,
    loading,
    refreshAccounts: async () => {
      try {
        const accountsData = await accountsApi.listOrgAccounts();
        setAccounts(accountsData || []);
      } catch (error) {
      }
    }
  };

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
};
